import { useRef, useState } from "react";
import { Play, Pause, MapPin, CheckCircle2, Phone, ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { VIDEO_SHOWCASE, BUSINESS } from "@/lib/siteData";

const POINTS = [
  "Leaf & debris clearance",
  "Garden tidy-ups & weeding",
  "Seasonal & regular maintenance",
  "Left clean, neat and tidy",
];

export const VideoShowcase = () => {
  const videoRef = useRef(null);
  const [playing, setPlaying] = useState(false);

  const toggle = async () => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      try {
        await v.play();
        setPlaying(true);
      } catch (_) {
        setPlaying(false);
      }
    } else {
      v.pause();
      setPlaying(false);
    }
  };

  return (
    <section
      id="video"
      data-testid="video-section"
      className="py-20 sm:py-28 lg:py-32 bg-green-950 text-white"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          {/* Left: video */}
          <div
            data-testid="video-tile"
            onClick={toggle}
            className="group relative rounded-3xl overflow-hidden cursor-pointer shadow-2xl shadow-black/40 aspect-[4/5] sm:aspect-square bg-black mx-auto w-full max-w-md lg:max-w-none"
          >
            <video
              ref={videoRef}
              src={VIDEO_SHOWCASE.src}
              poster={VIDEO_SHOWCASE.poster}
              playsInline
              loop
              preload="metadata"
              className="w-full h-full object-cover"
              onPlay={() => setPlaying(true)}
              onPause={() => setPlaying(false)}
            />
            <div className="absolute inset-0 bg-gradient-to-t from-green-950/70 via-transparent to-transparent pointer-events-none" />

            <button
              type="button"
              aria-label={playing ? "Pause video" : "Play video"}
              data-testid="video-play-button"
              onClick={(e) => {
                e.stopPropagation();
                toggle();
              }}
              className={`absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-18 h-18 sm:w-20 sm:h-20 rounded-full bg-green-600 hover:bg-green-700 text-white flex items-center justify-center transition-all ${
                playing ? "opacity-0 group-hover:opacity-100" : "opacity-100"
              }`}
              style={{ width: "5rem", height: "5rem" }}
            >
              {playing ? (
                <Pause className="w-8 h-8" />
              ) : (
                <Play className="w-8 h-8 ml-1" fill="currentColor" />
              )}
            </button>

            <div className="absolute bottom-0 inset-x-0 p-5 flex items-center gap-2 text-sm text-white/85 pointer-events-none">
              <MapPin className="w-4 h-4 text-green-300" /> {VIDEO_SHOWCASE.location}
            </div>
          </div>

          {/* Right: info */}
          <div>
            <span className="inline-block text-green-400 font-semibold text-sm uppercase tracking-widest">
              See the transformation
            </span>
            <h2
              data-testid="video-headline"
              className="mt-3 text-3xl sm:text-4xl lg:text-5xl font-extrabold tracking-tight"
              style={{ fontFamily: "Outfit, sans-serif" }}
            >
              {VIDEO_SHOWCASE.title}
            </h2>
            <p className="mt-4 text-white/70 text-base lg:text-lg leading-relaxed">
              {VIDEO_SHOWCASE.caption}
            </p>

            <ul className="mt-7 space-y-3">
              {POINTS.map((p) => (
                <li key={p} className="flex items-center gap-3 text-white/90">
                  <CheckCircle2 className="w-5 h-5 text-green-400 shrink-0" />
                  <span>{p}</span>
                </li>
              ))}
            </ul>

            <div className="mt-9 flex flex-col sm:flex-row gap-4">
              <a href="#contact" data-testid="video-quote-link">
                <Button
                  size="lg"
                  className="bg-green-600 hover:bg-green-700 text-white rounded-full px-7 py-6 text-base font-semibold group"
                >
                  Get in Touch
                  <ArrowRight className="w-5 h-5 ml-2 transition-transform group-hover:translate-x-1" />
                </Button>
              </a>
              <a href={`tel:${BUSINESS.phoneTel}`} data-testid="video-call-link">
                <Button
                  size="lg"
                  variant="outline"
                  className="border-2 border-white/50 bg-transparent hover:bg-white hover:text-green-900 text-white rounded-full px-7 py-6 text-base font-semibold"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  {BUSINESS.phone}
                </Button>
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default VideoShowcase;

